import React, { useCallback, useEffect, useState } from "react";
import { Drawer, Input, InputNumber, Select, Switch, message } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../../../core/data/redux/store";

import { createTdsTaxRegime, fetchTdsTaxRegimes, updateTdsTaxRegime } from "../../../../core/data/redux/payroll/TDS/tdsTaxRegime/tdsTaxRegimeActions";
import { fetchFinancialYearsByCompanyId } from "../../../../core/data/redux/financialYear/financialYearActions";

const TdsTaxRegimeModal: React.FC<any> = ({ modalType, handleCancel, isModalVisible, selectedRowData }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector((state: any) => state.auth);
  const { loading } = useSelector((state: any) => state.TDS.tdsTaxRegime);
  const { activeCompany } = useSelector((state: any) => state.companyDetails);
  const { financialYearsByCompany } = useSelector((state: any) => state.financialYears);
  const companyId = activeCompany?.company_detail_id
  const isLoading = modalType === "edit" ? loading : loading;
  const [formData, setFormData] = useState({
    RegimeName: "",
    FinancialYearID: null as number | null,
    IsDefault: false,
    AllowsStandardDeduction: false,
    StandardDeductionAmount: 0,
    AllowsChapterVI_ADeductions: false,
    RebateUnderSection87A_MaxIncome: 0,
    RebateUnderSection87A_Amount: 0,
    Status: "active",
  });

  const [errors, setErrors] = useState<any>({});

  const resetForm = useCallback(() => {
    setFormData({
      RegimeName: "",
      FinancialYearID: null as number | null,
      IsDefault: false,
      AllowsStandardDeduction: false,
      StandardDeductionAmount: 0,
      AllowsChapterVI_ADeductions: false,
      RebateUnderSection87A_MaxIncome: 0,
      RebateUnderSection87A_Amount: 0,
      Status: "active",
    });
    setErrors({});
  }, []);

  useEffect(() => {
    if (companyId) {
      dispatch(fetchFinancialYearsByCompanyId(companyId))
    }
  }, [dispatch, companyId])

  useEffect(() => {
    if (modalType === "edit" && selectedRowData) {
      setFormData({
        RegimeName: selectedRowData.RegimeName || "",
        FinancialYearID: selectedRowData.FinancialYearID != null ? Number(selectedRowData.FinancialYearID) : null,
        IsDefault: selectedRowData.IsDefault || false,
        AllowsStandardDeduction: selectedRowData.AllowsStandardDeduction || false,
        StandardDeductionAmount: selectedRowData.StandardDeductionAmount || 0,
        AllowsChapterVI_ADeductions: selectedRowData.AllowsChapterVI_ADeductions || false,
        RebateUnderSection87A_MaxIncome: selectedRowData.RebateUnderSection87A_MaxIncome || 0,
        RebateUnderSection87A_Amount: selectedRowData.RebateUnderSection87A_Amount || 0,
        Status: selectedRowData.Status || "active",
      });
    } else {
      resetForm();
    }
  }, [modalType, selectedRowData, resetForm, user?.UserId]);

  const handleChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev: any) => ({ ...prev, [field]: "" }));
  };

  const validateForm = () => {
    let valid = true;
    const newErrors: any = {};

    if (!formData.RegimeName.trim()) {
      newErrors.RegimeName = "Regime Name is required.";
      valid = false;
    }

    if (formData.FinancialYearID == null) {
      newErrors.FinancialYearID = "Financial Year is required";
      valid = false;
    }

    if (formData.StandardDeductionAmount < 0) {
      newErrors.StandardDeductionAmount = "Cannot be negative.";
      valid = false;
    }

    if (formData.RebateUnderSection87A_MaxIncome < 0) {
      newErrors.RebateUnderSection87A_MaxIncome = "Cannot be negative.";
      valid = false;
    }

    if (formData.RebateUnderSection87A_Amount < 0) {
      newErrors.RebateUnderSection87A_Amount = "Cannot be negative.";
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    const payload = {
      ...formData,
      ...(modalType === "edit"
        ? { ModifiedBy: user?.UserId || 1 }
        : { CreatedBy: user?.UserId || 1 }),
    };

    try {
      if (modalType === "edit") {
        await dispatch(
          updateTdsTaxRegime({ id: selectedRowData?.TaxRegimeID, ...payload })
        ).unwrap();
        message.success("Tax regime updated successfully");
      } else {
        await dispatch(createTdsTaxRegime(payload)).unwrap();
        message.success("Tax regime created successfully");
      }
      handleCancel();
      resetForm();
      await dispatch(fetchTdsTaxRegimes());
    } catch (error) {
      message.error("Something went wrong. Please try again.");
    }
  };

  return (
    <Drawer
      title={modalType === "edit" ? "Edit Tax Regime" : "Add Tax Regime"}
      placement="right"
      width={520}
      onClose={() => {
        handleCancel();
        resetForm();
      }}
      visible={isModalVisible}
    >
      <div className="mb-3">
        <label className="form-label">Regime Name <span style={{ color: 'red' }}>*</span></label>
        <Input
          value={formData.RegimeName}
          onChange={(e) => handleChange("RegimeName", e.target.value)}
        />
        {errors.RegimeName && <div className="text-danger">{errors.RegimeName}</div>}
      </div>

      <div className="mb-3">
        <label className="form-label">
          Financial Year <span style={{ color: 'red' }}>*</span>
        </label>
        <Select
          value={formData.FinancialYearID}
          onChange={(val) => handleChange("FinancialYearID", val)}
          className="w-100"
          placeholder="Select financial year"
          allowClear
          options={
            (financialYearsByCompany || []).map((fy: any) => ({
              value: fy.id,
              label: fy.name,
            }))
          }
        />
        {errors.FinancialYearID && (
          <div className="text-danger">{errors.FinancialYearID}</div>
        )}
      </div>

      <div className="mb-3">
        <label className="form-label">Standard Deduction Amount</label>
        <Input
          min={0}
          value={formData.StandardDeductionAmount}
          onChange={(e: any) => {
            const val = e.target.value;
            if (val === "") {
              handleChange("StandardDeductionAmount", "");
            } else if (/^\d*\.?\d*$/.test(val)) { // regex allows numbers with optional decimal point
              handleChange("StandardDeductionAmount", Number(val));
            }
          }}
          style={{ width: "100%" }}
        />
        {errors.StandardDeductionAmount && (
          <div className="text-danger">{errors.StandardDeductionAmount}</div>
        )}
      </div>

      <div className="mb-3">
        <label className="form-label">Max Income for 87A Rebate</label>
        <Input
          min={0}
          value={formData.RebateUnderSection87A_MaxIncome}
          onChange={(e: any) => {
            const val = e.target.value;
            if (val === "") {
              handleChange("RebateUnderSection87A_MaxIncome", 0);
            } else if (/^\d*\.?\d*$/.test(val)) {
              handleChange("RebateUnderSection87A_MaxIncome", Number(val));
            }
          }}
          style={{ width: "100%" }}
        />
        {errors.RebateUnderSection87A_MaxIncome && (
          <div className="text-danger">{errors.RebateUnderSection87A_MaxIncome}</div>
        )}
      </div>

      <div className="mb-3">
        <label className="form-label">87A Rebate Amount</label>
        <Input
          min={0}
          value={formData.RebateUnderSection87A_Amount}
          onChange={(e: any) => {
            const val = e.target.value;
            if (val === "") {
              handleChange("RebateUnderSection87A_Amount", 0);
            } else if (/^\d*\.?\d*$/.test(val)) {
              handleChange("RebateUnderSection87A_Amount", Number(val));
            }
          }}
          style={{ width: "100%" }}
        />
        {errors.RebateUnderSection87A_Amount && (
          <div className="text-danger">{errors.RebateUnderSection87A_Amount}</div>
        )}
      </div>

      <div className="mb-3">
        <label className="form-label">Status</label>
        <Select
          value={formData.Status}
          onChange={(value) => handleChange("Status", value)}
          className="w-100"
        >
          <Select.Option value="active">Active</Select.Option>
          <Select.Option value="inactive">Inactive</Select.Option>
        </Select>
      </div>

      <div className="mb-3 d-flex justify-content-between">
        <div>
          <label className="form-label me-2">Is Default</label>
          <Switch
            checked={formData.IsDefault}
            onChange={(val) => handleChange("IsDefault", val)}
          />
        </div>
        <div>
          <label className="form-label me-2">Allows Standard Deduction</label>
          <Switch
            checked={formData.AllowsStandardDeduction}
            onChange={(val) => handleChange("AllowsStandardDeduction", val)}
          />
        </div>
        <div>
          <label className="form-label me-2">Allows Chapter VI-A</label>
          <Switch
            checked={formData.AllowsChapterVI_ADeductions}
            onChange={(val) => handleChange("AllowsChapterVI_ADeductions", val)}
          />
        </div>
      </div>

      <div className="modal-footer mt-4">
        <button className="btn btn-secondary me-2" onClick={handleCancel}>
          Cancel
        </button>
        <button
          className="btn btn-primary"
          onClick={handleSubmit}
          disabled={isLoading}
        >
          {isLoading && <span className="spinner-border spinner-border-sm me-2" />}
          {modalType === "edit" ? "Update Regime" : "Create Regime"}
        </button>
      </div>
    </Drawer>
  );
};

export default TdsTaxRegimeModal;
